/* eslint-disable no-unused-vars */

export type TEventType = 'Click' | 'DoubleClick' | 'Set' | 'Select' | 'Deselect' | 'DeselectAnItem' | 'Change' | 'Validate' | 'ValidateState' | 'Assert' | 'Check' | 'Uncheck' | 'SendKey' | 'Type' | 'Form' | 'Other' | 'Expand' | 'Collapse' | 'Exists';
export type TWindowType = 'window' | 'frame' | 'modal';
export type TDefinitionType = 'tag' | 'role';
export type TTarget = 'Background' | 'Content' | 'Index' | 'Devpanel';
export type TSelector = 'xpath' | 'css'

type ResourceType =
  | 'main_frame'
  | 'sub_frame'
  | 'stylesheet'
  | 'script'
  | 'image'
  | 'object'
  | 'object_subrequest'
  | 'xmlhttprequest'
  | 'xslt'
  | 'ping'
  | 'beacon'
  | 'xml_dtd'
  | 'font'
  | 'media'
  | 'websocket'
  | 'csp_report'
  | 'imageset'
  | 'web_manifest'
  | 'speculative'
  | 'other';

/** Tracking flags that match our internal tracking classification */
type UrlClassificationFlags =
  | 'fingerprinting'
  | 'fingerprinting_content'
  | 'cryptomining'
  | 'cryptomining_content'
  | 'tracking'
  | 'tracking_ad'
  | 'tracking_analytics'
  | 'tracking_social'
  | 'tracking_content'
  | 'any_basic_tracking'
  | 'any_strict_tracking'
  | 'any_social_tracking';

/** If the request has been classified this is an array of `UrlClassificationFlags`. */
type UrlClassificationParty = UrlClassificationFlags[];

interface UrlClassification {
  /** Classification flags if the request has been classified and it is first party. */
  firstParty: UrlClassificationParty;
  /**
   * Classification flags if the request has been classified and it or its window hierarchy is third party.
   */
  thirdParty: UrlClassificationParty;
}

/**
 * An array of HTTP headers. Each header is represented as a dictionary containing the keys `name` and either `value` or `binaryValue`.
 */
type HttpHeaders = _HttpHeaders[];

interface _HttpHeaders {
  /** Name of the HTTP header. */
  name: string;
  /** Value of the HTTP header if it can be represented by UTF-8. */
  value?: string | undefined;
  /**
   * Value of the HTTP header if it cannot be represented by UTF-8, stored as individual byte values (0..255).
   */
  binaryValue?: number[] | undefined;
}

/** Contains the HTTP request body data. Only provided if extraInfoSpec contains 'requestBody'. */
interface _OnBeforeRequestDetailsRequestBody {
  /** Errors when obtaining request body data. */
  error?: string | undefined;
  /**
   * If the request method is POST and the body is a sequence of key-value pairs encoded in UTF8, encoded as either multipart/form-data, or application/x-www-form-urlencoded, this dictionary is present and for each key contains the list of all values for that key. If the data is of another media type, or if it is malformed, the dictionary is not present. An example value of this dictionary is {'key': ['value1', 'value2']}.
   */
  formData?: object | undefined;
  /**
   * If the request method is PUT or POST, and the body is not already parsed in formData, then the unparsed request body elements are contained in this array.
   */
  raw?: UploadData[] | undefined;
}

/** Contains data uploaded in a URL request. */
interface UploadData {
  /** An ArrayBuffer with a copy of the data. */
  bytes?: any;
  /** A string with the file's path and name. */
  file?: string | undefined;
}

export enum EAlertType {
  info,
  success,
  error,
  warn,
  verbose
}

export enum EMatch {
  Any,
  All,
}

export enum ENamespace {
  PM,
  CWS,
  CFMS,
  MSO,
}

export enum ESelectorType {
  DocumentTitle,
  WindowUrl,
  QuerySelector,
}

export enum EOperator {
  Equals,
  EqualsStrict,
  NotEquals,
  NotEqualsStrict,
  GreaterThan,
  LessThan,
  GreaterOrEqual,
  LessOrEqual,
  Contains,
  NotContains,
}

export enum ECondition {
  is,
  contains,
  equals,
  isNot,
  notEqual,
  notContains,
  exists,
}

export enum EStatus {
  Pending,
  Done,
}

export enum ESection {
  Steps,
  Routes,
  Validation,
  Options
}


export interface IAny {
  [key: string]: any;
}

export interface IRoute {
  id: string
  enabled: boolean
  pattern: string
  project: number
  frames: ITabFrame[]
}

export interface ITabFrame {
  tabId: number
  frameId: number
}

export interface IMessage {
  target: TTarget
  action: string
  value?: any
}

export interface IIdentifier {
  Field: string
  Unknown?: string
  Table?: string
  Indirect?: string
}

export interface ISettings {
  recording: boolean
  demoServer: boolean
  showTooltips: boolean
  deleteWarning: boolean
  detailedLogs: boolean
  logLevels: ILogLevels
  specialKeys: boolean
  pressKeys: IPressKeys
  activeSection: ESection
  autoValidate: boolean
  takeScreenshots: boolean
  windowWidth: number
  windowHeight: number
  windowTop: number
  windowLeft: number
}

export interface ILogLevels {
  error: boolean
  warn: boolean
  info: boolean
  verbose: boolean
}

export interface ICapturedEvents {
  click: boolean
  keys: boolean
  input: boolean
  dialog: boolean
}

export interface IScanOptions {
  includeFrames: boolean
  limitUrl: boolean
  matchPattern?: string
  includeHidden: boolean
  highlightSelected: boolean
  specificRoles: boolean
  selectedRoles?: string[]
}

export interface IPressKeys {
  ENTER: boolean
  TAB: boolean
  ESCAPE: boolean
  BACKSPACE: boolean
  CAPSLOCK: boolean
  DELETE: boolean
  ARROWS: boolean
  F5: boolean
}

export interface IProject {
  ProjectID: number
  Name: string
  ExcludedSelectors?: string
  Extensions?: IExtension[]
}

export interface IPost {
  BrowserID: number,
  TestScenarioID: number,
  TestSteps: IInputStep[],
  Requests: IRequest[],
  AfterStep: number
}

export interface ITest {
  projectid: number
  testid: number
  testname: string
  steps: number
}

export interface ITestStep {
  order: number
  description: string
}

export interface IExtension {
  Name: string
  Title: string
  SourceCode: string
  Icon?: string
  Description?: string
  Method?: Function
  Silent?: boolean
  Parameters?: IExtensionParameter[]
  Values?: { [key: string]: any }
}

export interface IExtensionParameter {
  Name: string
  DisplayName?: string
  DataType: string
  Description?: string
  Required: boolean
  DefaultValue?: string
  AvailableValues?: string
  IsValid?: boolean
}

export interface IAppVersion {
  Name: string
  Beta: boolean
  Major: number
  Minor: number
  Patch: number
}

export interface IAppUpdate {
  Name: string
  Created: string
  Description: string
}

export interface IValidatedForm {
  id?: number
  eid?: string
  name?: string
  alert?: string
}

export interface IValidateOptions {
  text: boolean
  inputs: boolean
  tables: boolean
  buttons: boolean
  labels: boolean
  disabled: boolean
  images: boolean
  states: boolean
}

export interface IForm {
  Name: string
  ProjectID: number
  FormID: number
  ExternalID: string | null
  Projects?: IProject[]
  NameSpace?: ENamespace | null
  Alert?: string | null
  Locked?: boolean
  Status?: string | null
}

export interface IExternalID {
  ExternalID?: string
  NameSpace?: ENamespace
}

export interface ISessionState {
  start: number
  recording: boolean
  controlling: boolean
  auto_validate: boolean
  warn_deleting: boolean
  enable_console: boolean
  record_keys: boolean
  validate_options: IValidateOptions
  api_key?: string
  scenario_id?: number
  scenario_name?: string
}

export interface IEvent {
  uid: string
  event: TEventType
  url: string
  form: IEventForm
  time: number
  fid: string
  fields: IField[]
  trusted: boolean
  group?: string
  table?: ITable
  key?: string
  keys?: IKeys
  validate?: IValidation
  description?: string
}

export interface IEventForm {
  name: string
  id: string
  source?: string
  alert?: string
  rec?: DOMRect
  element?: any
}

export interface ITable {
  tid: string
  name: string
  headers: any[]
  rows: any[]
  selected: number
  checked?: boolean
}

export interface ITarget {
  field: IField
  action: TEventType
  element?: any
  definition?: IFieldDefinition
  form?: IEventForm
}

export interface IPosition {
  type: TSelector
  nodes: number
  total?: number
  ordinal?: number
  error?: string
}

export interface IField {
  fid: string
  attributes: IAttributes
  selectors: ISelectors
  coordinates?: string
  ordinal?: number
  maxOrdinal?: number
  element?: any
  window?: TWindowType
  alert?: IFieldAlert
  type?: string
  tag?: string
  label?: string
  label_source?: string
  ownerForm?: IEventForm
}

export interface IXPath {
  path: string
  source: string
}

export interface ISelectors {
  xpath?: string
  xpath_source?: string
  xpath2?: string
  xpath2_source?: string
  css?: string
  css2?: string
  shadowXpath?: string
  shadowXpath_source?: string
  shadowCss?: string
}

export interface IFieldDefinition {
  name: string
  type: TDefinitionType
  role?: string
  clickable?: boolean
  action?: string
  parent?: string
  label?: string
  children?: string[]
}

export interface IFieldAlert {
  message: string
  type: EAlertType
}

export interface IKeys {
  ctrl?: boolean
  shift?: boolean
  alt?: boolean
}

export interface IAttributes {
  href?: string
  title?: string
  id?: string
  id_source?: string
  name?: string
  value?: string
  text?: string
  type?: string
  classes?: string
  checked?: string
  disabled?: string
  required?: string
  pressed?: string
  invalid?: string
  hidden?: string
  expanded?: string
  readonly?: string
  selected?: string
  role?: string
  aria?: string
}

export interface ILocation {
  protocol?: string
  host?: string
  hostname?: string
  port?: string
  path?: string
  page?: string
  search?: string
  query?: any
  href?: string
  hash?: string
}

export interface IValidation {
  name: string
  value: any
  condition: ECondition
  type: 'Validate' | 'Assert'
  column?: number
}

export interface INodeProps {
  name: string
  value: any
  required?: boolean
  operator?: EOperator
}

export interface IDialogState {
  _curTop?: any
  _curLeft?: any
  _curRight?: any
  _curBottom?: any
  _curHeight?: any
  _curWidth?: any
}

export interface ISaveResponse {
  SaveSuccess?: boolean
  PostSuccess?: boolean
  PostResults?: any
  SaveResults?: string
}

export interface IFrame {
  window: Window
  document: any
  loaded: boolean
  script: boolean
  element: boolean
  done: boolean
  counter: number
  error?: string
}

export interface IAlertOptions {
  title: string
  body: string
  status?: EAlertType
  ok?: boolean
  yes?: boolean
  no?: boolean
  cancel?: boolean
  excludeBackdrop?: boolean
}

export interface IFetched {
  success: boolean
  data?: any
  error?: string
}

export interface IExtensionResult {
  success: boolean
  message: string
  results: any[]
}

export interface IAuth {
  prodKey?: string
  demoKey?: string
}

export interface IStep {
  event: IEvent
  routes: IRoute[]
  switches: ISwitch[]
  form?: IForm
  tabId?: number
  frameId?: number
  notes?: string | null
  screenshot?: string | null
  noScreenshot?: boolean
}

export interface IExportStep {
  form: string
  step: number
  description: string
  xpath: string
  css: string
  switch: boolean
  notes: string
}

export interface IExportData {
  extension: string
  blob: Blob
}

export interface ISwitch {
  type: TWindowType
  url?: string
}

export interface IFrameInfo {
  tabId: number
  frameId: number
  parentFrameId?: number
  url?: string
}

export interface IActionProps {
  ItemName?: string
  Value?: string
  Description?: string
  Condition?: string
  AttributeName?: string
  AttributeValue?: string
  KeyName?: string
  ControlKey?: string
  Negate?: boolean
  SelectType?: string
  ComparisonType?: string
  ValueDataType?: string
  WhenNotValid?: string
  Timeout?: number
}

export interface IElementID {
  attribute: string
  value: string
}

export interface IDomElement {
  path: any[]
  node: any
}

export interface IInputStep {
  Order: number
  Recorded: string
  Form: IForm
  Action: IInputAction
  Field: IInputField | null
  Notes: string | null
  Switches: string | null
  Screenshot: string | null
  Coordinates: string | null
  Location: number | null
}

export interface IInputAction {
  Name: string
  Properties: IActionProps
}

export interface IInputField {
  Name: string
  FieldType: string | null
  Id: string | null
  XPath: string | null
  XPath2: string | null
  Css: string | null
  Css2: string | null
  ShadowXpath: string | null
  ShadowCss: string | null
}

export interface IResult {
  Success: boolean
  ResultValue?: any
  ErrorMessage?: string
  ErrorMessages?: string[]
}

export interface IMenuItem {
  text?: string
  icon?: string
  enabled?: boolean
  sub?: IMenuItem[]
  type?: any
  events?: { [key: string]: Function }
}

export interface IRequest {
  ID: string
  Order?: number
  Timestamp: number
  Url: string
  Method: string
  Protocol: string
  Port?: string | null
  RelativePath?: string | null
  Headers?: string | null
  Arguments?: string | null
}

export interface IRequestDetail {
  /**
   * The ID of the request. Request IDs are unique within a browser session. As a result, they could be used to relate different events of the same request.
   */
  requestId: string;
  url: string;
  /** Standard HTTP method. */
  method: string;
  /**
   * The value 0 indicates that the request happens in the main frame; a positive value indicates the ID of a subframe in which the request happens. If the document of a (sub-)frame is loaded (`type` is `main_frame` or `sub_frame`), `frameId` indicates the ID of this frame, not the ID of the outer frame. Frame IDs are unique within a tab.
   */
  frameId: number;
  /** ID of frame that wraps the frame which sent the request. Set to -1 if no parent frame exists. */
  parentFrameId: number;
  /** True for private browsing requests. */
  incognito?: boolean;
  /** The cookie store ID of the contextual identity. */
  cookieStoreId?: string;
  /** URL of the resource that triggered this request. */
  originUrl?: string;
  /** URL of the page into which the requested resource will be loaded. */
  documentUrl?: string;
  /** The ID of the tab in which the request takes place. Set to -1 if the request isn't related to a tab. */
  tabId: number;
  /** How the requested resource will be used. */
  type: ResourceType;
  /** The time when this signal is triggered, in milliseconds since the epoch. */
  timeStamp: number;
  /** The HTTP request headers that have been sent out with this request. */
  requestHeaders?: HttpHeaders;
  /** Contains the HTTP request body data. Only provided if extraInfoSpec contains 'requestBody'. */
  requestBody?: _OnBeforeRequestDetailsRequestBody;
  /** Tracking classification if the request has been classified. */
  urlClassification?: UrlClassification;
  /** Indicates if this request and its content window hierarchy is third party. */
  thirdParty: boolean;
}

export interface IRequestHeaders {
  ID: string
  Headers: string
}

export interface IRequestHeader {
  Name?: string
  Value?: string
}

export interface IRequestArgument {
  Name?: string
  Value?: string
}

export interface IScanResults {
  elements: number
  warnings: number
  forms: IScannerFormData[]
  error?: string
}

export interface IScannerFormData extends IForm {
  fid: string
  Source: string
  Path: string
  Frame: boolean
  FrameID?: number
  Elements?: IScannerElementData[]
  Alerts: IScannerAlert[]
  Routes: IRoute[]
}

export interface IScannerElementData {
  uid: number
  fid: string
  Form: IEventForm
  Tag: string
  Code: string
  Role: string
  Type: string
  Label: string
  LabelSource: string
  Text: string
  ID: string
  IdSource: string
  Selector: string
  Visible: boolean
  Alerts: IScannerAlert[]
}

export interface IScannerAlert {
  Type: string
  Severity: EAlertType
  Message: string
}

export interface IScannerTableValidation {
  valid: boolean
  reasons: string[]
}

export interface IScanProperty {
  alerts?: IScannerAlert[]
  label: string
  value: string
}

export interface IScannerExportData {
  _switch: boolean
  form: string
  formSource: string
  frame: string
  tag?: string
  role?: string
  type?: string
  label?: string
  labelSource?: string
  text?: string
  id?: string
  name?: string
  selector?: string
  alerts?: string
}

export interface IStyleAttributes {
  'border-top-width': number
  'border-right-width': number
  'border-bottom-width': number
  'border-left-width': number
  'margin-top': number
  'margin-right': number
  'margin-bottom': number
  'margin-left': number
  'padding-top': number
  'padding-right': number
  'padding-bottom': number
  'padding-left': number
  'z-index': number
  'width': number
  'height': number
  'left': number
  'top': number
}

export interface IStylePosition {
  left: number
  top: number
}

export interface IHighlightOptions {
  fid: string
  uid: string
  visible?: boolean
}

export interface IEventParams {
  bubbles?: any
  cancelable?: any
  detail: any
}

export interface ILabel {
  text: string
  source: string
}

export interface CssPathStep {
  value: string
  optimized: boolean
}
